"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.aiSdkProvider = void 0;
const __1 = require("../..");
const json_1 = require("../../../core/utils/json");
const media_1 = require("../../../core/normalize/message-parts/media");
const tool_calls_1 = require("../../../core/normalize/message-parts/tool-calls");
const tool_results_1 = require("../../../core/normalize/message-parts/tool-results");
const tool_definitions_1 = require("../../../core/normalize/tool-definitions");
/**
 * Vercel AI SDK convention: this module owns AI SDK's typed part vocabulary
 * and the `ai.prompt.tools` OTel attribute. `aiSdkFilePart` lives in
 * the canonical media builders because the `image`/`file` handlers use the
 * same media-reference mechanics.
 */
// The canonical FinishReason vocabulary is adopted from the AI SDK, so this
// map is the identity.
const AI_SDK_FINISH_REASON_TYPE_BY_RAW = {
    stop: "stop",
    length: "length",
    "tool-calls": "tool-calls",
    "content-filter": "content-filter",
    error: "error",
    other: "other",
    unknown: "unknown",
};
/**
 * AI SDK file payloads (`data`, legacy `image`, nested reasoning `file`):
 * raw base64 bytes, a URL string, or tagged {type: "data" | "url"} shapes.
 */
function aiSdkFilePart(payload, options = {}) {
    const tagged = (0, json_1.asRecord)(payload);
    const candidate = (0, json_1.optionalString)(tagged?.data ?? tagged?.url ?? payload);
    if (!candidate)
        return null;
    const reference = (0, media_1.parseMediaReference)(candidate);
    if (reference) {
        return (0, json_1.compact)({
            ...(0, media_1.filePartFromMediaReference)(reference),
            filename: options.filename,
        });
    }
    const isUrl = tagged?.type === "url" ||
        (tagged !== undefined && tagged.url !== undefined && !tagged.data) ||
        (!tagged && /^(https?:|data:)/.test(candidate));
    return (0, json_1.compact)({
        type: "file",
        mediaType: options.mediaType ??
            (0, media_1.mediaTypeFromDataUri)(candidate) ??
            options.fallbackMediaType,
        filename: options.filename,
        content: isUrl
            ? { kind: "url", url: candidate }
            : { kind: "base64", data: candidate },
    });
}
const normalizeAiSdkImage = (value) => {
    // Legacy AI SDK image parts carry the payload directly under `image`/
    // `data`; source-wrapped `image` blocks on the same type name belong to
    // the Anthropic dialect and fall through.
    if ((0, json_1.asRecord)(value.source))
        return __1.unmatched;
    const part = aiSdkFilePart(value.image ?? value.data, {
        mediaType: (0, json_1.optionalString)(value.mediaType),
        fallbackMediaType: "image/*",
    });
    return part ? (0, __1.claimed)(part) : __1.unmatched;
};
const normalizeAiSdkFile = (value) => {
    // AI SDK file parts carry a flat `data`/`url` payload; `file`-wrapped
    // blocks on the same type name belong to the OpenAI chat dialect and fall
    // through.
    if ((0, json_1.asRecord)(value.file))
        return __1.unmatched;
    const part = aiSdkFilePart(value.data ?? value.url, {
        mediaType: (0, json_1.optionalString)(value.mediaType),
        filename: (0, json_1.optionalString)(value.filename),
    });
    return part ? (0, __1.claimed)(part) : __1.unmatched;
};
const normalizeAiSdkToolError = (value) => {
    const raw = (0, json_1.parseIfString)(value.error ?? null);
    return (0, __1.claimed)((0, json_1.compact)({
        type: "tool-result",
        toolCallId: (0, json_1.nullableString)(value.toolCallId),
        toolName: (0, json_1.optionalString)(value.toolName),
        output: typeof raw === "string"
            ? { type: "text", value: raw }
            : { type: "json", value: (0, json_1.toJsonValue)(raw) },
        isError: true,
    }));
};
const normalizeAiSdkReasoningFile = (value) => {
    // AI SDK reasoning-generated file (a FilePart nested under `file`; older
    // emissions used a flat `data`): it is still a file to every consumer.
    const nested = (0, json_1.asRecord)(value.file);
    const part = aiSdkFilePart(nested?.data ?? nested?.url ?? value.data, {
        mediaType: (0, json_1.optionalString)(nested?.mediaType ?? value.mediaType),
        filename: (0, json_1.optionalString)(nested?.filename ?? value.filename),
    });
    return part ? (0, __1.claimed)({ ...part, reasoning: true }) : __1.unmatched;
};
const normalizeAiSdkSource = (value) => (0, __1.claimed)({
    // AI SDK source parts are stream-positioned references without a text
    // anchor, so they remain custom parts with their original payload.
    type: "custom",
    kind: "source",
    value: (0, json_1.toJsonValue)(value),
});
// AI SDK tool-call parts: { toolCallId, toolName, input } (older emissions
// used `args`).
const normalizeAiSdkToolCall = (value) => (0, __1.claimed)((0, tool_calls_1.toolCallPart)({
    toolCallId: value.toolCallId,
    toolName: value.toolName,
    input: value.input ?? value.args,
    toolType: (0, json_1.optionalString)(value.type),
    providerExecuted: value.providerExecuted,
}));
// AI SDK tool-result parts: { toolCallId, toolName, output } (v4 emissions
// used `result`).
const normalizeAiSdkToolResult = (value, context) => {
    const raw = value.output ?? value.result;
    const record = (0, json_1.asRecord)(raw);
    const wrapper = record && ("value" in record || record.type === "execution-denied")
        ? record
        : undefined;
    let output;
    let isError = typeof value.isError === "boolean" ? value.isError : undefined;
    switch (wrapper?.type) {
        case "text":
        case "error-text":
            output =
                typeof wrapper.value === "string"
                    ? { type: "text", value: wrapper.value }
                    : { type: "json", value: (0, json_1.toJsonValue)(wrapper.value) };
            isError = isError ?? (wrapper.type === "error-text" ? true : undefined);
            break;
        case "json":
        case "error-json":
            output = { type: "json", value: (0, json_1.toJsonValue)(wrapper.value) };
            isError = isError ?? (wrapper.type === "error-json" ? true : undefined);
            break;
        case "content":
            output = (0, tool_results_1.toolResultOutput)(Array.isArray(wrapper.value)
                ? context.normalizePartList(wrapper.value)
                : [], wrapper.value);
            break;
        case "execution-denied":
            output = { type: "json", value: (0, json_1.toJsonValue)(raw) };
            break;
        default: {
            const parsed = (0, json_1.parseIfString)(raw ?? null);
            output =
                typeof parsed === "string"
                    ? { type: "text", value: parsed }
                    : { type: "json", value: (0, json_1.toJsonValue)(parsed) };
        }
    }
    return (0, __1.claimed)((0, json_1.compact)({
        type: "tool-result",
        toolCallId: (0, json_1.nullableString)(value.toolCallId),
        toolName: (0, json_1.optionalString)(value.toolName),
        output,
        isError,
    }));
};
// Model-facing tool outputs use explicit URL/data variants rather than the
// legacy image/file payloads used in ordinary messages.
const normalizeAiSdkToolMedia = (value) => {
    const type = String(value.type);
    const payload = type.endsWith("-url")
        ? { type: "url", url: value.url }
        : { type: "data", data: value.data };
    const part = aiSdkFilePart(payload, {
        mediaType: (0, json_1.optionalString)(value.mediaType),
        filename: (0, json_1.optionalString)(value.filename),
        fallbackMediaType: type.startsWith("image-") ? "image/*" : undefined,
    });
    return part ? (0, __1.claimed)(part) : __1.unmatched;
};
const AI_SDK_PART_HANDLERS = {
    "image-data": normalizeAiSdkToolMedia,
    "image-url": normalizeAiSdkToolMedia,
    "file-data": normalizeAiSdkToolMedia,
    "file-url": normalizeAiSdkToolMedia,
    "tool-call": normalizeAiSdkToolCall,
    "tool-error": normalizeAiSdkToolError,
    "tool-result": normalizeAiSdkToolResult,
    "reasoning-file": normalizeAiSdkReasoningFile,
    source: normalizeAiSdkSource,
    image: normalizeAiSdkImage,
    file: normalizeAiSdkFile,
};
/**
 * The AI SDK's OTel instrumentation exports the request tools under the
 * `ai.prompt.tools` span attribute (provider built-ins may lack a name).
 */
function aiSdkToolDefinitionSources(carrier) {
    const tools = carrier.metadataAttributes?.["ai.prompt.tools"];
    if (tools === undefined)
        return [];
    return [
        {
            sourceKey: "ai.prompt.tools",
            value: tools,
            options: { allowProviderToolWithoutName: true, allowToolMap: true },
        },
    ];
}
exports.aiSdkProvider = {
    name: "ai-sdk",
    claimMessages: (root, kind) => {
        if (kind !== "input" || root.messages !== undefined)
            return [];
        const prompt = root.prompt;
        if (typeof prompt === "string") {
            (0, json_1.recordKeyAsParsed)(root, "prompt");
            return [{ kind: "single", value: prompt, fallbackRole: "user" }];
        }
        if (Array.isArray(prompt) &&
            prompt.every((message) => typeof (0, json_1.asRecord)(message)?.role === "string")) {
            (0, json_1.recordKeyAsParsed)(root, "prompt");
            return [{ kind: "sequence", values: prompt, fallbackRole: "user" }];
        }
        return [];
    },
    finishReasonTypeByRaw: AI_SDK_FINISH_REASON_TYPE_BY_RAW,
    // AI SDK / MCP tool declarations: { name?, description, inputSchema }.
    tryNormalizeToolDefinition: (value) => {
        if (value.inputSchema === undefined)
            return __1.unmatched;
        const definition = (0, tool_definitions_1.toolDefinition)({
            name: value.name,
            description: value.description,
            inputSchema: value.inputSchema,
            type: value.type,
            providerMetadata: (0, tool_definitions_1.toolDefinitionProviderMetadata)(value, value),
        });
        return definition ? (0, __1.claimed)(definition) : __1.unmatched;
    },
    // UIMessages carry a `parts` array; camelCase `toolCalls` siblings appear
    // in AI-SDK-flavored (and koog) logging.
    messageLikeKeys: new Set(["parts", "toolCalls"]),
    typedParts: AI_SDK_PART_HANDLERS,
    collectToolDefinitionSources: aiSdkToolDefinitionSources,
};
